// Saved-view logic for Tables: filtering, sorting and field visibility.
// Pure functions — no Firebase, no React — so the behaviour that decides
// "which rows and columns does this view show" is unit-testable.

// A view looks like:
// {
//   id, name,
//   type: 'grid' | 'kanban',
//   groupBy: fieldId | null,            // kanban column field (select type)
//   filters: [{ fieldId, op, value }],  // all must match (AND)
//   sort: { fieldId, dir: 1 | -1 } | null,
//   hiddenFieldIds: [fieldId, ...],
// }

export const FILTER_OPS = [
  { value: 'is',        label: 'is' },
  { value: 'is_not',    label: 'is not' },
  { value: 'contains',  label: 'contains' },
  { value: 'not_empty', label: 'is not empty' },
  { value: 'empty',     label: 'is empty' },
  { value: 'gt',        label: '>' },
  { value: 'lt',        label: '<' },
];

export function matchesFilter(record, filter) {
  const raw = record.values?.[filter.fieldId];
  const val = String(raw ?? '').trim();
  const want = String(filter.value ?? '').trim();
  switch (filter.op) {
    case 'is':        return val.toLowerCase() === want.toLowerCase();
    case 'is_not':    return val.toLowerCase() !== want.toLowerCase();
    case 'contains':  return val.toLowerCase().includes(want.toLowerCase());
    case 'not_empty': return val !== '';
    case 'empty':     return val === '';
    case 'gt':        return val !== '' && Number(val) > Number(want);
    case 'lt':        return val !== '' && Number(val) < Number(want);
    default:          return true;
  }
}

export function applyFilters(records, filters) {
  const active = (filters || []).filter(f => f.fieldId && f.op);
  if (!active.length) return records;
  return records.filter(r => active.every(f => matchesFilter(r, f)));
}

export function applySort(records, sort, fields) {
  if (!sort?.fieldId) return records;
  const f = (fields || []).find(x => x.id === sort.fieldId);
  const dir = sort.dir === -1 ? -1 : 1;
  return [...records].sort((a, b) => {
    const av = a.values?.[sort.fieldId] ?? '';
    const bv = b.values?.[sort.fieldId] ?? '';
    if (f?.type === 'number') return (Number(av) - Number(bv)) * dir;
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
  });
}

// Full view application: filters then sort.
export function applyView(records, view, fields) {
  if (!view) return records;
  return applySort(applyFilters(records, view.filters), view.sort, fields);
}

export function visibleFields(fields, view) {
  const hidden = new Set(view?.hiddenFieldIds || []);
  return (fields || []).filter(f => !hidden.has(f.id));
}

// Kanban grouping: one column per option of the groupBy select field, plus a
// trailing "(none)" column for records without a value. Records keep the
// view's filter/sort applied by the caller.
export const KANBAN_NONE = '__none__';

export function groupForKanban(records, groupByFieldId, field) {
  const options = field?.options || [];
  const cols = options.map(opt => ({ key: opt, label: opt, records: [] }));
  const none = { key: KANBAN_NONE, label: '(none)', records: [] };
  for (const r of records) {
    const v = String(r.values?.[groupByFieldId] ?? '').trim();
    const col = cols.find(c => c.key === v);
    (col || none).records.push(r);
  }
  return [...cols, none];
}
